/**
 * Report přepočtu ke stažení (fáze 5, rozhodnutí F5-A) — self-contained HTML
 * (souhrn + účastníci + automatické testy + AI kontrola) k tisku/archivaci.
 * Ve standalone mocku HTML; v BE (F7) se nahradí PDF. Žádný externí zdroj.
 */

import { Fund, UnitBatch } from '../../api/types'

const czk = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 2 })
const units = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 8 })
const percent = new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function dmy(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-')
  return d && m && y ? `${d}.${m}.${y}` : iso
}

/** Sestaví HTML reportu přepočtu. `navDeviationRatio` = odchylka NAV proti minulému ocenění (nebo null). */
export function buildBatchReportHtml(
  batch: UnitBatch,
  fund: Fund,
  navDeviationRatio: number | null
): string {
  const sigma = batch.totalUnitsAfter - batch.totalUnitsBefore
  const deviationText =
    navDeviationRatio === null ? 'první ocenění' : `${percent.format(navDeviationRatio * 100)} %`

  const participantRows = batch.participants
    .map((p) => {
      const price = p.priceOverride ?? batch.unitPrice
      const control = Math.round(p.units * price * 10000) / 10000
      const overrideNote =
        p.priceOverride != null || p.unitsOverride != null ? ' <em>(override)</em>' : ''
      return `<tr>
        <td>${esc(p.clientName)}${overrideNote}</td>
        <td class="num">${czk.format(p.amount)} Kč</td>
        <td class="num">${units.format(price)}</td>
        <td class="num">${units.format(p.units)}</td>
        <td class="num">${czk.format(control)} Kč</td>
      </tr>`
    })
    .join('\n')

  const testRows = (batch.check?.autoTests ?? [])
    .map(
      (t) => `<li class="${t.passed ? 'pass' : 'fail'}">
        <strong>${t.passed ? 'PASS' : 'FAIL'}</strong> — ${esc(t.name)}${
        t.detail ? `<br><span class="detail">${esc(t.detail)}</span>` : ''
      }</li>`
    )
    .join('\n')
  const allPassed =
    (batch.check?.autoTests?.length ?? 0) > 0 && batch.check!.autoTests.every((t) => t.passed)
  const verdict = allPassed ? 'VŠE OK' : 'NALEZENY CHYBY'

  const aiRows = (batch.check?.aiResults ?? [])
    .map((r) => {
      const name = batch.participants.find((p) => p.clientId === r.id)?.clientName ?? r.name
      const label = r.status === 'OK' ? 'V POŘÁDKU' : 'CHYBA'
      return `<li class="${r.status === 'OK' ? 'pass' : 'fail'}">
        <strong>${esc(name)} — ${label}</strong><br><span class="detail">${esc(r.comment)}</span>
      </li>`
    })
    .join('\n')

  return `<!doctype html>
<html lang="cs">
<head>
<meta charset="utf-8">
<title>Report přepočtu ${esc(batch.id)} — ${esc(fund.name)}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #171A1E; margin: 32px; }
  h1 { font-size: 20px; color: #0A1E3C; margin: 0 0 4px; }
  h2 { font-size: 14px; color: #0A1E3C; text-transform: uppercase; letter-spacing: .5px;
       border-bottom: 2px solid #0A1E3C; padding-bottom: 4px; margin: 24px 0 8px; }
  .sub { color: #5b6470; margin: 0 0 16px; }
  dl { display: grid; grid-template-columns: auto 1fr; column-gap: 24px; row-gap: 4px; margin: 0; }
  dt { color: #5b6470; } dd { margin: 0; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th, td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #e3e7ec; }
  th { text-transform: uppercase; font-size: 11px; color: #5b6470; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  ul { list-style: none; padding: 0; }
  li { padding: 6px 0; border-bottom: 1px solid #f0f2f5; }
  li.pass strong { color: #2A7E19; } li.fail strong { color: #D02F0B; }
  .detail { color: #5b6470; font-size: 12px; }
  .verdict { display: inline-block; padding: 2px 10px; border-radius: 9999px; font-weight: 700; }
  .verdict.ok { background: #e6f4e3; color: #2A7E19; }
  .verdict.err { background: #fbe4df; color: #D02F0B; }
  footer { margin-top: 32px; color: #8a929c; font-size: 11px; }
</style>
</head>
<body>
  <h1>Apex ${esc(fund.name)} — přepočet ${esc(batch.id)}</h1>
  <p class="sub">Stav: ${esc(batch.status)} · datum přidělení ${dmy(batch.assignmentDate)}</p>

  <h2>Souhrn</h2>
  <dl>
    <dt>NAV</dt><dd>${czk.format(batch.navTotal)} Kč</dd>
    <dt>Cena jednotky</dt><dd>${units.format(batch.unitPrice)}</dd>
    <dt>Jednotky před → po</dt><dd>${units.format(batch.totalUnitsBefore)} → ${units.format(batch.totalUnitsAfter)} (+${units.format(sigma)})</dd>
    <dt>Změna NAV vs. minulé ocenění</dt><dd>${deviationText}</dd>
    <dt>Vytvořil</dt><dd>${esc(batch.createdBy)} · ${dmy(batch.createdAt)}</dd>
    ${
      batch.decidedBy
        ? `<dt>Rozhodl</dt><dd>${esc(batch.decidedBy)} · ${dmy(batch.decidedAt ?? '')}</dd>`
        : ''
    }
  </dl>

  <h2>Účastníci</h2>
  <table>
    <thead><tr><th>Klient</th><th class="num">Vklad</th><th class="num">Cena</th><th class="num">Jednotky</th><th class="num">Kontrola (cena × jednotky)</th></tr></thead>
    <tbody>${participantRows}</tbody>
  </table>

  <h2>Automatické testy — <span class="verdict ${allPassed ? 'ok' : 'err'}">${verdict}</span></h2>
  <ul>${testRows || '<li class="detail">Testy nebyly spuštěny.</li>'}</ul>

  <h2>AI kontrola</h2>
  <ul>${aiRows || '<li class="detail">AI kontrola nebyla spuštěna.</li>'}</ul>

  <footer>Mock report Apex Ops (fáze 5) — v produkci nahradí PDF. Vygenerováno z dat modulu.</footer>
</body>
</html>`
}

/** Sestaví HTML report a stáhne ho jako soubor (mock — v BE PDF). */
export function downloadBatchReport(
  batch: UnitBatch,
  fund: Fund,
  navDeviationRatio: number | null
): void {
  const html = buildBatchReportHtml(batch, fund, navDeviationRatio)
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `report-prepocet-${batch.id}-${fund.key}.html`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
